import React, { useContext, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AccountContext } from "../store/AccountContext";
import { OverviewContext } from "../store/OverviewContext";
import { captureAttribution } from "../utils/attribution";
import { findLiveCampaign } from "../utils/liveCampaign";
import {
  matchLinkRoute,
  normaliseLinkPath,
  withLinkSource,
} from "../utils/linkRoutes";

// /link/<path> (#883): where link.farmflix.tv sends anyone who opens one of
// its links without the FarmFlix app. Every visit ends on another page:
//
//   1. a website page matching the app link path (utils/linkRoutes.ts);
//   2. otherwise the live marketing campaign with that slug (/c/<slug>);
//   3. otherwise /videos for a logged-in member, /app for everyone else.
//
// The query string is kept, and the arrival is recorded for attribution.
// Only traffic through /link/* reaches step 3: the site-wide NotFound route
// is unchanged.

type Destination = { to: string; campaignSlug?: string };

const LinkResolver = () => {
  const params = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();

  const {
    overview,
    loading: overviewLoading,
    error: overviewError,
  } = useContext(OverviewContext);
  const {
    loggedIn,
    validated,
    loading: accountLoading,
  } = useContext(AccountContext);

  const path = normaliseLinkPath(params["*"]);
  const pagePath = matchLinkRoute(path);

  // Steps 2 and 3 have to wait for the overview (its live campaign list) and
  // for the login check. A failed overview counts as "no live campaign", as
  // it does on the campaign page itself. `validated` rather than `loading`
  // alone: loading still reads false on the first render, before the stored
  // session has been checked.
  const overviewSettled = !!overview || (!!overviewError && !overviewLoading);
  const accountSettled = validated && !accountLoading;

  let destination: Destination | undefined;
  if (pagePath) {
    destination = { to: pagePath };
  } else if (overviewSettled && accountSettled) {
    const campaign = findLiveCampaign(overview, path);
    if (campaign) {
      destination = { to: `/c/${campaign.slug}`, campaignSlug: campaign.slug };
    } else {
      destination = { to: loggedIn ? "/videos" : "/app" };
    }
  }

  const to = destination?.to;
  const campaignSlug = destination?.campaignSlug;

  useEffect(() => {
    if (!to) {
      return;
    }

    const forwardSearch = withLinkSource(search);
    captureAttribution(forwardSearch, campaignSlug);
    navigate(`${to}${forwardSearch}`, { replace: true });
  }, [to, campaignSlug, search, navigate]);

  return (
    <div
      data-testid="link-resolving"
      style={{ display: "flex", justifyContent: "center", padding: 64 }}
    >
      <CircularProgress color="primary" />
    </div>
  );
};

export default LinkResolver;
